import {
  faChevronDown,
  faChevronUp,
  faInfoCircle,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { MouseEventHandler, useState, useEffect } from "react";
import { Button } from "../components/ui/button";
import TickText from "@/components/TickText";
import { PriceResultProps } from "@/lib/type";
import CarDetailTile from "@/components/CarDetailTile";
import ReactGA from "react-ga4";
import { Tooltip } from "@/components/ui/tooltip";
import { formatCurrencyAmount } from "@/lib/utils";
import { useRegion } from "@/context/RegionContext";
import { getRegionConfigByCode } from "@/lib/regionConfig";

interface CarItemProps {
  index: number;
  car: PriceResultProps;
  onSelect: MouseEventHandler;
  rentalDays: number;
  overlayActive?: boolean;
}

const CarItem: React.FC<CarItemProps> = ({index,car,onSelect,rentalDays,overlayActive = false}) => {
  const [isExpand, setIsExpand] = useState(false);
  const [isOverlayDetected, setIsOverlayDetected] = useState(false);
  const { region } = useRegion();
  const basicProtectionExcess = getRegionConfigByCode(
    car._region ?? region.code,
  ).protection.basicExcess;

  // Simple detection for the specific address dropdown
  useEffect(() => {
    const checkForAddressDropdown = () => {
      const addressDropdown = document.querySelector('[id*="placeCard"]');
      let isVisible = false;
      
      if (addressDropdown) {
        const styles = window.getComputedStyle(addressDropdown);
        const rect = addressDropdown.getBoundingClientRect();
        // Check if element is truly visible and has dimensions
        isVisible = styles.display !== 'none' && 
                   styles.visibility !== 'hidden' && 
                   styles.opacity !== '0' &&
                   rect.width > 0 && 
                   rect.height > 0;
      }
      
      if (isVisible !== isOverlayDetected) {
        setIsOverlayDetected(isVisible);
        console.log('Address dropdown state changed:', isVisible, addressDropdown ? 'element exists' : 'no element');
      }
    };

    // Check every 100ms (simple polling)
    const interval = setInterval(checkForAddressDropdown, 100);
    
    // Also listen for clicks outside to reset state
    const handleClickOutside = (event) => {
      const dropdown = document.querySelector('[id*="placeCard"]');
      if (dropdown && !dropdown.contains(event.target) && isOverlayDetected) {
        console.log('Click outside detected, resetting overlay state');
        setIsOverlayDetected(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    
    return () => {
      clearInterval(interval);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOverlayDetected]);

  // Track add to cart event for GA4
  const trackAddToCartEvent = () => {
    const isDebugMode = import.meta.env.VITE_GA_DEBUG === 'true';
    const GA4_MEASUREMENT_ID = import.meta.env.VITE_GA_MEASUREMENT_ID || 'G-X72S1Z5RKE';
    
    try {
      // Prepare event parameters
      const eventParams = {
        // Standard ecommerce parameters
        currency: car.pricing.currency || 'MYR',
        value: car.pricing.total_with_tax * rentalDays,
        // Items array for enhanced measurement
        items: [{
          item_id: car.group.id,
          item_name: car.group.name,
          item_category: 'Vehicle',
          price: car.pricing.total_with_tax,
          quantity: rentalDays,
          currency: car.pricing.currency || 'MYR'
        }],
        // Purchase funnel data
        cart_step: 3  // Indicate this is step 3 in the funnel
      };

      // Debug logs
      if (isDebugMode) {
        console.group('GA4 Add to Cart Event');
        console.log('Event Name:', 'add_to_cart');
        console.log('Event Parameters:', eventParams);
        console.log('GA4 Initialized:', typeof window.gtag === 'function');
        console.log('Environment:', import.meta.env.VITE_BUILD_ENV);
        console.log('Debug Mode:', isDebugMode);
        console.log('Purchase Funnel Step:', 3);
        console.log('Vehicle Selected:', car.group.name);
        console.log('Total Value:', car.pricing.total_with_tax * rentalDays);
      }
      
      // Send event to GA4
      if (typeof window.gtag === 'function') {
        // Configure debug mode for this event
        if (isDebugMode) {
          window.gtag('config', GA4_MEASUREMENT_ID, {
            debug_mode: true
          });
        }
        
        window.gtag('event', 'add_to_cart', eventParams);
        isDebugMode && console.log('GA4 event sent via gtag');
      }
      
      // Send via ReactGA
      ReactGA.event('add_to_cart', eventParams);
      isDebugMode && console.log('GA4 event sent via ReactGA');
      
      isDebugMode && console.groupEnd();
    } catch (error) {
      console.error('Error sending GA4 add_to_cart event:', error);
      
      // Fallback to direct gtag if available
      try {
        if (typeof window.gtag === 'function') {
          const basicParams = {
            currency: car.pricing.currency || 'MYR',
            value: car.pricing.total_with_tax * rentalDays,
            items: [{
              item_id: car.group.id,
              item_name: car.group.name,
              item_category: 'Vehicle'
            }]
          };
          window.gtag('event', 'add_to_cart', basicParams);
          console.log('Fallback GA4 event sent');
        }
      } catch (fallbackError) {
        console.error('Error sending fallback GA4 event:', fallbackError);
      }
    }
  };

  // Handle select button click
  const handleSelectClick: MouseEventHandler = (e) => {
    // Track the add_to_cart event
    trackAddToCartEvent();
    
    // Call the original onSelect handler
    onSelect(e);
  };

  return (
    <div className="mb-3">
      <div className="grid grid-rows-1 gap-4 mt-4 md:grid-cols-4 md:items-center">
        <div className="relative">
          <img src={car.group_detail.image_url} className="md:w-full w-screen" />
          {car._region && car._region !== region.code && (
            <span className="absolute top-2 left-2 bg-blue-600 text-white text-xs font-bold px-2 py-1 rounded">
              Available in {car._region === "MY" ? "Malaysia" : "Thailand"}
            </span>
          )}
        </div>
        <div className="col-span-2 flex flex-col font-bold w-fit space-y-3">
          <p>{car.group.name}</p>
          <div className="rounded-full border-2 pl-3 pr-1 py-1 flex flex-row items-center space-x-2 w-fit">
            <span className="text-xs">OR SIMILAR ECONOMIQUE</span>
            <Tooltip 
              id={`similar-info-${index}`}
              title=""
              content={<span className="text-left font-normal">The model you get on your pick up day is subject to availability.</span>}
              className={(overlayActive || isOverlayDetected) ? 'pointer-events-none' : ''}
            >
              <FontAwesomeIcon 
                icon={faInfoCircle} 
                className={`text-neutral-300 ${
                  (overlayActive || isOverlayDetected)
                    ? 'opacity-30 relative z-[-1]' 
                    : ''
                }`} 
              />
            </Tooltip>
          </div>
          <CarDetailTile {...car.group_detail} />
          <TickText
            id={`benefits-${index}`}
            itemList={["Included Milleage", "Unlimited Protection"]}
          />
          <p
            className="font-bold text-green-600 cursor-pointer"
            onClick={() => setIsExpand(!isExpand)}
          >
            {isExpand ? "Less Details " : "More Details "}
            <FontAwesomeIcon icon={isExpand ? faChevronUp : faChevronDown} />
          </p>
        </div>
        <div className="justify-self-end text-end">
          <p className="text-xs">PAY ONLINE</p>
          <p>
            <span className="font-bold text-lg">{formatCurrencyAmount(car.pricing.total_with_tax, car.pricing.currency)}</span> / day
          </p>
          <p className="text-gray-500 text-sm md:mb-2 mb-4">TOTAL {formatCurrencyAmount(car.pricing.total_with_tax * rentalDays, car.pricing.currency)}</p>
          <Button className="w-[92vw] md:w-fit" onClick={handleSelectClick}>Select</Button>
        </div>
      </div>
      {isExpand ? (
        <div className="mt-2">
          <div className="bg-neutral-100 p-5">
            <p className="font-bold text-lg">Full vehicle details</p>
          </div>
          <div className="bg-neutral-100 p-5 mt-2">
            <p className="text-red-500 font-medium text-sm">INCLUDED</p>
            <p className="font-bold text-lg mb-2">Basic protection</p>
            <p className="font-bold mb-2">Excess: {formatCurrencyAmount(basicProtectionExcess, car.pricing.currency)}</p>
            <TickText
              id={`protection-${index}`}
              itemList={["Collision Damage Protection", "Theft Protection"]}
            />
            <p className="text-gray-600 mt-2">
              <FontAwesomeIcon icon={faInfoCircle} /> You will be able to
              upgrade your protection after selecting this vehicle
            </p>
          </div>
        </div>
      ) : (
        <></>
      )}
    </div>
  );
};

export default CarItem;
